import { useContext, useMemo } from 'react';

import { useRouteMatch } from 'react-router-dom';

import { AppId } from '../constants';
import { Context, RouterProps } from '../routers/router-context';
import { OptionalAppRoute } from '../types';
import { doesLegacyViewMatch } from '../utils/doesLegacyViewMatch';
import { doParamsAgree } from '../utils/doParamsAgree';

/**
 * Whether the page in view is the given route. The match must be exact.
 *
 * A navigation link does not use this: it elects the current link inside its group, with
 * `useNavLink`, and that election matches a prefix.
 */
const doesRouteMatch = (currentContext: RouterProps, match: any, route?: OptionalAppRoute<any>) => {
    /** If the route contains an external url, return directly. */
    if (route?.appId === AppId.external) {
        return false;
    }

    /**
     * For the routing on the legacy side, we have a context that provides the current view,
     * and we reuse it in order to determine the current view.
     *
     * TODO: remove this once we have migrated to full react
     */
    if (currentContext && currentContext.currentView) {
        return doesLegacyViewMatch(currentContext, route);
    }

    return Boolean(match?.isExact) && doParamsAgree(route, match?.params);
};

/**
 * Returns true if the current location matches the provided route
 * @param route - route to match against
 */
const useRouteMatching = (route?: OptionalAppRoute<any>): boolean => {
    const match = useRouteMatch(route?.path || '');
    const currentContext = useContext(Context);

    return useMemo(() => doesRouteMatch(currentContext, match, route), [currentContext, match, route]);
};

export { useRouteMatching, doesRouteMatch };
